/**
 * Provider of database-persisted, per-user-and-page state.
 *
 * This works like SenseStateProvider for Ext.js and is initialized through ApplicationSettingsManagementHook.
 *
 * @packageDocumentation
 */

import { createRequest, HttpMethod } from './Ajax';
import { showError } from './Error';
import { useMemo, useRef, useState } from 'react';
import { decodeExtState, encodeExtState } from './internal/ExtState';
import { isEmptyString } from '../util';

/**
 * Type of the state.
 *
 */
type NamedState = { name: string; value: string };

/**
 * Class responsible for tracking and updating persistent states in the `customuserprofile` database table.
 * These state values are used for e.g. saving states of Tables to improve the user experience and similar such things.
 *
 */
class PersistentStateProvider {
  /** Initial state when the page was loaded. */
  private initialState: Record<string, unknown> = {};
  /** Base url used to persist and retrieve the state. */
  private url?: string = undefined;
  /** Name of the workflow that the state is related to. */
  private workflowName?: string = undefined;
  /** Queue for all the state changes that should be persisted. */
  private stateChangeQueue: Array<NamedState> = [];

  /**
   * Initializes the {@link PersistentStateProvider}
   *
   * @param url
   * @param workflowName
   * @param initialState
   */
  public init({
    url,
    workflowName,
    initialState
  }: {
    url: string;
    workflowName: string;
    initialState: Array<NamedState>;
  }): void {
    this.url = url;
    this.workflowName = workflowName;

    // decode the initial state
    initialState.forEach(item => {
      this.initialState[item.name] = decodeExtState(item.value);
    });

    setInterval(this.persistChanges, 5000);
  }

  /**
   * Arrow function for correct `this` semantics
   */
  private persistChanges = async (): Promise<void> => {
    if (!this.url || !this.workflowName) {
      console.warn(
        'PersistentStateProvider was not initialized correctly: not persisting state'
      );
      return;
    }
    if (this.stateChangeQueue.length === 0) {
      console.debug('no changes, no need to persist state');
      return;
    }

    await saveStates({
      url: this.url,
      workflowName: this.workflowName,
      stateData: this.stateChangeQueue
    }).catch(showError);

    // clear queue
    this.stateChangeQueue = [];
  };

  /**
   * Getter to retrieve the state for a specific name key.
   *
   * Important Note: The state value returned here is ONLY the initial state.
   * If this state changes, such changes will not be trackable through the state value returned from this method.
   *
   * @param name key of the state
   * @return an array of
   *
   *  1. the initial value of the state
   *  2. a function to update the value to a new state
   */
  public getPersistentState<T>(
    name?: string
  ): [T | null | undefined, (newState: T | null | undefined) => void] {
    if (isEmptyString(name)) {
      return [undefined, () => undefined];
    }

    return [
      this.initialState[name] as T,
      newState =>
        this.stateChangeQueue.push({ name, value: encodeExtState(newState) })
    ];
  }
}

/**
 * Simple helper type to define a type to be either its original type or null or undefined.
 */
type ValueOrNotDefined<T> = T | null | undefined;

/**
 * Hook to use a persistent state with a specific key.
 *
 * @param key key of the state
 * @return an array of
 *
 *  1. current value of the state
 *  2. a function to update the value to a new state
 */
export function usePersistentPreferences<T>(
  key?: string
): [ValueOrNotDefined<T>, (newState: ValueOrNotDefined<T>) => void] {
  const [initialState, persistState] = useMemo(
    () => persistentStateProvider.getPersistentState<T>(key),
    [key]
  );

  const [state, setState] = useState(initialState);
  const returnFunc = useRef((newState: ValueOrNotDefined<T>) => {
    setState(newState);
    persistState(newState);
  });

  return [state, returnFunc.current];
}

/**
 * Ajax request to the ApplicationSettingsManagementHook 'saveState' EndpointMethod to save state changes.
 */
const saveStates = createRequest<
  {
    workflowName: string;
    stateData: Array<NamedState>;
  },
  void
>({ method: HttpMethod.POST, operation: 'saveState' });

/**
 * The instance of the {@link PersistentStateProvider}.
 */
export const persistentStateProvider = new PersistentStateProvider();
